/**
 * A list somebody types on one line, separated by commas.
 *
 * The parsing is trivial; the reason this is a module is the round trip. A
 * field that renders `values.join(', ')` and parses every keystroke back into
 * `values` cannot be typed into: the comma you press is parsed away and the
 * input is redrawn without it, so "Dipirona, Dipirona Sódica" is unreachable
 * one character in. Keeping the text and the values as two things, and asking
 * whether they still mean the same, is what makes the field typeable.
 */

/** The values in a typed line, blanks and stray separators dropped. */
export function parseCommaList(text: string): string[] {
  return text
    .split(',')
    .map((one) => one.trim())
    .filter((one) => one !== '')
}

/** The canonical line for a list of values. What the field settles to. */
export function formatCommaList(values: readonly string[]): string {
  return values.join(', ')
}

/**
 * Whether typed text still means exactly these values.
 *
 * The question a field asks before overwriting what somebody is typing: a
 * half-typed "Dipirona," *means* `['Dipirona']` and must be left alone, while
 * values that arrived from somewhere else -- a reload, another case -- mean
 * something this text does not, and the text has to go.
 */
export function sameCommaList(text: string, values: readonly string[]): boolean {
  const parsed = parseCommaList(text)
  return parsed.length === values.length && parsed.every((one, index) => one === values[index])
}
